// src/components/ArkanaGuide/ArkanaGuide.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import './ArkanaGuide.css';

// ===========================
// STORAGE HELPERS
// ===========================
const STORAGE_KEY = 'arkana_prefs';
const SEEN_KEY = 'arkana_seen_tips';
const VISIT_KEY = 'arkana_visit_count';
const ONBOARD_KEY = 'arkana_onboarded';

const getPrefs = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};
const savePrefs = (p) => localStorage.setItem(STORAGE_KEY, JSON.stringify(p));

const getSeenTips = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || {}; } catch { return {}; }
};
const saveSeenTips = (s) => localStorage.setItem(SEEN_KEY, JSON.stringify(s));

const incrementVisit = (page) => {
  try {
    const v = JSON.parse(localStorage.getItem(VISIT_KEY)) || {};
    v[page] = (v[page] || 0) + 1;
    localStorage.setItem(VISIT_KEY, JSON.stringify(v));
    return v[page];
  } catch { return 1; }
};

const isOnboarded = () => localStorage.getItem(ONBOARD_KEY) === 'true';
const setOnboarded = () => localStorage.setItem(ONBOARD_KEY, 'true');

// ===========================
// SOUND HELPER
// ===========================
const playPop = (() => {
  let ctx = null;
  return (enabled) => {
    if (!enabled) return;
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
    } catch {}
  };
})();

// ===========================
// TIME-BASED GREETING
// ===========================
const getGreeting = (name) => {
  const h = new Date().getHours();
  const n = name ? `, ${name}` : '';
  if (h >= 5 && h < 12) return `Buenos días${n}! ☀️`;
  if (h >= 12 && h < 18) return `Buenas tardes${n}!`;
  if (h >= 18 && h < 22) return `Buenas noches${n}! 🌙`;
  return `Operando tarde${n}? 🦉 El mercado no duerme!`;
};

// ===========================
// ONBOARDING STEPS
// ===========================
const ONBOARDING = (name) => [
  {
    text: `${getGreeting(name)} Soy Arkana, tu guía. Acá el protagonista sos vos — yo solo te muestro el camino.`,
    action: null,
  },
  {
    text: 'Lo primero, lo más importante: tu plata es tuya. Vive en tu wallet, no en Bitplify. Vos tenés la única llave 🔑',
    action: null,
  },
  {
    text: 'Por eso nadie puede tocar tus fondos — ni nosotros. Si te da dudas, en el Centro de Seguridad te explico todo sin vueltas.',
    action: null,
  },
  {
    text: 'El Marketplace es donde sucede todo. Vos ponés tu precio y elegís con quién operás: el mercado lo hacés vos.',
    action: 'Ve al Marketplace desde el menú lateral.',
  },
  {
    text: 'Cada trader tiene un perfil con Trust Score, nivel y badges. La confianza se gana, no se inventa — revisá la reputación antes de operar.',
    action: null,
  },
  {
    text: 'Cuando estés listo, creá tu primera orden desde "Crear Orden" en el menú.',
    action: null,
  },
  {
    text: 'En Game Rewards ganás XP y subís de nivel con cada trade. ¡Revisalo!',
    action: null,
  },
  {
    text: '¡Listo! Ya conocés lo básico. Hacé click cuando quieras para más tips. Tu plata, tu llave, tu decisión. 🐾',
    action: null,
  },
];

// ===========================
// EASTER EGGS (1 in 15 chance)
// ===========================
const EASTER_EGGS = [
  '¿Sabías que el primer Bitcoin se usó para comprar 2 pizzas? Hoy valdrían millones. 🍕',
  'Satoshi Nakamoto, el creador de Bitcoin, nunca ha revelado su identidad. Misterioso...',
  'El total de Bitcoin que existirá jamás es 21 millones. Ni uno más.',
  'La palabra "HODL" nació de un typo en un foro en 2013. Hoy es un estilo de vida.',
  'Ethereum fue propuesto por Vitalik Buterin cuando tenía solo 19 años.',
  'Se estima que 20% de todos los Bitcoin están perdidos para siempre en wallets olvidadas.',
  'El bloque génesis de Bitcoin tiene un mensaje oculto de un titular de periódico.',
  'Sigue así, cada trade te acerca más a la cima. 💪',
  'Pro tip: los mejores traders operan con paciencia, no con impulso.',
  'La consistencia le gana al volumen. Un trade diario vale más que 10 trades impulsivos.',
  'Fun fact: hay más direcciones de Bitcoin posibles que átomos en el universo observable.',
  'Arkana dice: "El que no arriesga, no gana... pero el que arriesga sin pensar, pierde todo." 🐾',
  'Tu plata, tu llave. Si no controlás las llaves, no es del todo tuya. 🔑',
  'Las stablecoins no se devalúan de un día para otro como el peso. Por eso tanta gente en LATAM las usa para guardar valor.',
  'Autocustodia = tu dinero no depende de que una empresa siga existiendo. Depende de vos. 🐾',
];

// ===========================
// FAQ - MINI CHATBOT
// ===========================
const FAQ = [
  { q: '¿Qué es el spread?', a: 'El spread es la diferencia entre el precio de tu oferta y el precio de mercado. Negativo = descuento, Positivo = ganancia, 0% = precio exacto del mercado.' },
  { q: '¿Cómo subo de nivel?', a: 'Completa trades para ganar XP. Cada trade suma puntos. También puedes hacer misiones diarias en Game Rewards para XP extra.' },
  { q: '¿Qué es el Trust Score?', a: 'Es tu puntuación de confianza. Sube con trades completados, respuestas rápidas y sin disputas. Los traders lo ven antes de operar contigo.' },
  { q: '¿Cómo creo una orden?', a: 'Ve a "Crear Orden" en el menú. Elige compra o venta, define el monto, spread, blockchain y método de pago. ¡Listo!' },
  { q: '¿Qué son los BP Coins?', a: 'Son monedas del juego que ganas con misiones y trades. Se canjean por beneficios reales como spreads preferenciales en la Tienda.' },
  { q: '¿Es seguro operar aquí?', a: 'Sí. Tu plata vive en tu wallet, no en Bitplify — nadie puede tocarla más que vos. Además usamos reputación, Trust Score y verificación. Siempre revisá el perfil del trader antes de operar.' },
  { q: '¿Quién controla mi plata?', a: 'Solo vos. Bitplify es un mercado P2P: conectamos a las partes, pero no custodiamos tus fondos. Tu plata, tu llave. Si querés entenderlo en simple, pasá por el Centro de Seguridad.' },
];

// ===========================
// TIPS POR PÁGINA
// ===========================
const TIPS = {
  '/': (name) => ({
    first: `${getGreeting(name)} Hay ofertas activas en el Marketplace.`,
    tips: [
      'Filtra por tu país para ver traders locales y evitar comisiones de cambio.',
      'El spread en VERDE significa que el trader te da descuento sobre el mercado.',
      'Spread en ROJO significa que pagas más caro — compara antes de ofertar.',
      'La columna RESP. muestra qué tan rápido responde el trader — busca menos de 3 min.',
      'La barra de confianza en amarillo/rojo indica trader nuevo o con quejas.',
      'Filtra por Blockchain para operar solo en la red que ya tienes.',
      'Usa el filtro de Método de Pago para encontrar traders que acepten tu banco.',
      'Los traders con racha activa son los más confiables del momento.',
      'Haz click en Ofertar para abrir el chat directo con el trader.',
      'El precio TRM opera al precio oficial del mercado — sin markup ni descuento.',
      'Más trades completados = más experiencia y confianza del trader.',
    ],
  }),
  '/create': (name) => ({
    first: `${getGreeting(name)} Crea tu orden eligiendo bien el tipo: Compra o Venta.`,
    tips: [
      'Spread 0% = operas exactamente al precio de mercado (TRM).',
      'Spread negativo (-2%, -3%) = descuento al comprador — atraes más traders rápido.',
      'Spread positivo (+2%, +3%) = ganas más por trade, pero tardas más en encontrar contraparte.',
      'Elige bien tu Blockchain — cada red tiene costos de gas distintos.',
      'El método de pago es lo primero que ven los traders — pon el más popular de tu país.',
      'Puedes crear múltiples órdenes activas al mismo tiempo.',
      'Define un mínimo y máximo de USDT por trade para controlar el riesgo.',
      'Las órdenes con spread verde aparecen primero en el Marketplace.',
    ],
  }),
  '/trades': (name) => ({
    first: `${getGreeting(name)} Revisa tus trades activos y completados.`,
    tips: [
      'Cada trade completado suma XP y te acerca al siguiente nivel.',
      'Los trades en verde = completados | Amarillo = en proceso | Rojo = cancelados.',
      'Si un trade quedó pendiente más de 30 min, puedes recordarle al trader.',
      'Tu racha de días activos seguidos da XP bonus — no la rompas.',
      'Revisa los trades con disputa — resolverlos mejora tu Trust Score.',
      'Filtra por moneda para ver cuánto volumen moviste en BTC, ETH, SOL, etc.',
      'Un trade cancelado no suma XP pero tampoco baja tu score si no abriste disputa.',
    ],
  }),
  '/profile': (name) => ({
    first: `${getGreeting(name)} Tu perfil es tu carta de presentación ante otros traders.`,
    tips: [
      'Completa el KYC para desbloquear límites más altos de operación.',
      'Activa el 2FA — protege tu cuenta aunque alguien tenga tu contraseña.',
      'Agrega todas tus cuentas bancarias para aceptar más métodos de pago.',
      'Tu Trust Score sube con trades completados y respuestas rápidas.',
      'La insignia Élite (Nv.400+) genera más confianza desde el primer mensaje.',
      'Los traders con foto de perfil reciben más ofertas — sube una buena imagen.',
      'Revisa tus notificaciones activas — no te pierdas ningún trade entrante.',
    ],
  }),
  '/rewards': (name) => ({
    first: `${getGreeting(name)} Aquí subes de nivel como trader.`,
    tips: [
      'Cada trade completado suma XP — a más XP, más alto tu nivel Élite.',
      'Completa las misiones diarias para ganar BP Coins extra.',
      'Los BP Coins se canjean en la Tienda por beneficios como spreads preferenciales.',
      'El Leaderboard muestra los traders con más volumen — ¿puedes entrar al Top 10?',
      'Los niveles más altos desbloquean la insignia Élite visible en el Marketplace.',
      'Las rachas consecutivas dan multiplicador de XP.',
      'Los trofeos aparecen por hitos: primer trade, 10 trades, 100 trades.',
      'Subir de nivel aumenta tu límite diario de operación.',
    ],
  }),
};

const DEFAULT_TIPS = (name) => ({
  first: `${getGreeting(name)} Soy Arkana, tu guía en Bitplify.`,
  tips: [
    'Usa el menú lateral para navegar entre Marketplace, Trades y tu Perfil.',
    '¿Primera vez aquí? Empieza explorando el Marketplace y filtra por tu país.',
    'Cada trade que completas suma XP y te acerca al rango Élite.',
  ],
});

// ===========================
// MODES: tips | onboarding | faq
// ===========================
export default function ArkanaGuide() {
  const location = useLocation();
  const { user } = useAuth();
  const name = user?.name ?? user?.username ?? null;
  const page = location.pathname;

  const [dismissed, setDismissed] = useState(() => getPrefs().dismissed === true);
  const [quietMode, setQuietMode] = useState(() => getPrefs().quiet === true);
  const [soundOn, setSoundOn] = useState(() => getPrefs().sound !== false);
  const [minimized, setMinimized] = useState(false);
  const [tipIdx, setTipIdx] = useState(-1);
  const [showBubble, setShowBubble] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);

  // Modes
  const [mode, setMode] = useState('tips'); // 'tips' | 'onboarding' | 'faq'
  const [onboardStep, setOnboardStep] = useState(0);
  const [showFaq, setShowFaq] = useState(false);
  const [faqAnswer, setFaqAnswer] = useState(null);

  const timerRef = useRef(null);
  const autoHideRef = useRef(null);
  const scrollStateRef = useRef({ showBubble: false, showFaq: false, mode: 'tips' });

  // Swipe to dismiss (mobile)
  const touchStartRef = useRef(null);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const handleTouchStart = (e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setSwiping(false);
    setSwipeX(0);
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    // Only track horizontal swipes to the right
    if (dx > 10 && Math.abs(dy) < 40) {
      setSwiping(true);
      setSwipeX(Math.max(0, dx));
    }
  };

  const handleTouchEnd = () => {
    if (swiping && swipeX > 80) {
      // Dismiss with swipe
      setSwipeX(300);
      setTimeout(() => {
        savePrefs({ ...getPrefs(), dismissed: true });
        setDismissed(true);
        setSwipeX(0);
        setSwiping(false);
      }, 250);
    } else {
      setSwipeX(0);
      setSwiping(false);
    }
    touchStartRef.current = null;
  };

  // Build tips
  const tipsData = (TIPS[page] ?? DEFAULT_TIPS)(name);
  const allTips = [tipsData.first, ...tipsData.tips];

  const markTipSeen = useCallback((index) => {
    const seen = getSeenTips();
    const pk = page || '/';
    if (!seen[pk]) seen[pk] = [];
    if (!seen[pk].includes(index)) { seen[pk].push(index); saveSeenTips(seen); }
  }, [page]);

  // Check if should do onboarding
  useEffect(() => {
    if (!isOnboarded() && !dismissed) {
      setMode('onboarding');
      setOnboardStep(0);
      setShowBubble(true);
    }
  }, [dismissed]);

  // On page change (tips mode)
  useEffect(() => {
    if (mode === 'onboarding') return;
    setMode('tips');
    setShowFaq(false);
    setFaqAnswer(null);
    setTipIdx(-1);
    setShowBubble(false);
    setExiting(false);
    setMinimized(false);

    if (dismissed || quietMode) return;

    const visitNum = incrementVisit(page);

    if (visitNum <= 3) {
      const delay = visitNum === 1 ? 1500 : 3000;
      timerRef.current = setTimeout(() => {
        // Easter egg chance
        if (Math.random() < 1 / 15) {
          setTipIdx(-99); // special marker for easter egg
          setShowBubble(true);
          playPop(soundOn);
          return;
        }
        setTipIdx(0);
        setShowBubble(true);
        markTipSeen(0);
        playPop(soundOn);
      }, delay);
    } else {
      setNudge(true);
      timerRef.current = setTimeout(() => setNudge(false), 1500);
    }

    return () => { clearTimeout(timerRef.current); clearTimeout(autoHideRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dismissed, quietMode, mode]);

  // Auto-hide (tips mode only)
  useEffect(() => {
    if (!showBubble || mode === 'onboarding' || mode === 'faq') return;
    clearTimeout(autoHideRef.current);
    autoHideRef.current = setTimeout(() => hideBubble(), 14000);
    return () => clearTimeout(autoHideRef.current);
  }, [showBubble, tipIdx, mode]);

  // Mantener referencia fresca del estado para el listener de scroll (sin re-bindear)
  scrollStateRef.current = { showBubble, showFaq, mode };

  // Auto-ocultar al scrollear hacia abajo; reaparecer al subir (no estorba mientras navegás)
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      if (Math.abs(dy) < 6) return;          // ignora micro-movimientos
      const s = scrollStateRef.current;
      // No esconder si está hablando, en FAQ u onboarding (el usuario está leyendo)
      if (!s.showBubble && !s.showFaq && s.mode !== 'onboarding') {
        setScrollHidden(dy > 0 && y > 120);  // baja → ocultar; sube (o cerca del tope) → mostrar
      } else {
        setScrollHidden(false);
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hideBubble = () => {
    setExiting(true);
    setTimeout(() => { setShowBubble(false); setExiting(false); }, 250);
  };

  // Easter egg text
  const getEasterEgg = () => EASTER_EGGS[Math.floor(Math.random() * EASTER_EGGS.length)];

  const nextTip = () => {
    if (mode === 'onboarding') {
      if (onboardStep < ONBOARDING(name).length - 1) {
        setOnboardStep(s => s + 1);
        playPop(soundOn);
      } else {
        setOnboarded();
        setMode('tips');
        hideBubble();
      }
      return;
    }
    // Easter egg chance on next
    if (Math.random() < 1 / 15) {
      setTipIdx(-99);
      playPop(soundOn);
      return;
    }
    const next = (tipIdx + 1) % allTips.length;
    setTipIdx(next);
    setShowBubble(true);
    setExiting(false);
    markTipSeen(next);
    playPop(soundOn);
  };

  const handleSpriteClick = () => {
    if (minimized) {
      setMinimized(false);
      setTipIdx(0);
      setShowBubble(true);
      setExiting(false);
      markTipSeen(0);
      playPop(soundOn);
      return;
    }
    if (showFaq) return; // Don't interfere with FAQ
    if (!showBubble) {
      setTipIdx(0);
      setShowBubble(true);
      setExiting(false);
      markTipSeen(0);
      playPop(soundOn);
      return;
    }
    nextTip();
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    hideBubble();
    setShowFaq(false);
    setFaqAnswer(null);
    setTimeout(() => setMinimized(true), 260);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    savePrefs({ ...getPrefs(), dismissed: true });
    setDismissed(true);
  };

  const handleQuietMode = (e) => {
    e.stopPropagation();
    savePrefs({ ...getPrefs(), quiet: true });
    hideBubble();
    setQuietMode(true);
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundOn;
    setSoundOn(next);
    savePrefs({ ...getPrefs(), sound: next });
    if (next) playPop(true);
  };

  const toggleFaq = (e) => {
    e.stopPropagation();
    if (showFaq) {
      setShowFaq(false);
      setFaqAnswer(null);
      setMode('tips');
    } else {
      setShowFaq(true);
      setShowBubble(false);
      setFaqAnswer(null);
      setMode('faq');
    }
  };

  const handleFaqClick = (idx) => {
    setFaqAnswer(idx);
    playPop(soundOn);
  };

  const handleReopen = () => {
    savePrefs({ dismissed: false, quiet: false, sound: soundOn });
    setDismissed(false);
    setQuietMode(false);
    setMinimized(false);
    setTipIdx(0);
    setShowBubble(true);
    setExiting(false);
    markTipSeen(0);
    playPop(soundOn);
  };

  // Dismissed
  if (dismissed) {
    return (
      <button className="ark-reopen-btn" onClick={handleReopen} title="Abrir Arkana">
        <img src="/assets/arkana-idle.gif" alt="Arkana" draggable={false} />
      </button>
    );
  }

  // Current content
  let bubbleContent = null;
  let bubbleFooter = null;

  if (mode === 'onboarding' && showBubble) {
    const step = ONBOARDING(name)[onboardStep];
    bubbleContent = (
      <>
        <p className="ark-bubble-text">{step.text}</p>
        {step.action && <p className="ark-bubble-action">→ {step.action}</p>}
      </>
    );
    bubbleFooter = (
      <div className="ark-bubble-footer">
        <span className="ark-bubble-hint ark-onboard-label">Onboarding</span>
        <span className="ark-bubble-count">{onboardStep + 1}/{ONBOARDING(name).length}</span>
      </div>
    );
  } else if (mode === 'tips' && showBubble && !minimized) {
    const isEasterEgg = tipIdx === -99;
    const text = isEasterEgg ? getEasterEgg() : (tipIdx >= 0 && tipIdx < allTips.length ? allTips[tipIdx] : null);
    if (text) {
      bubbleContent = <p className="ark-bubble-text">{text}</p>;
      bubbleFooter = (
        <div className="ark-bubble-footer">
          <span className="ark-bubble-hint">
            {isEasterEgg ? '✨ Fun fact' : 'Click → siguiente'}
          </span>
          {!isEasterEgg && (
            <span className="ark-bubble-count">{tipIdx + 1}/{allTips.length}</span>
          )}
        </div>
      );
    }
  }

  return (
    <div
      className={`ark-wrapper ${minimized ? 'ark-minimized' : ''} ${swiping ? 'ark-swiping' : ''} ${scrollHidden ? 'ark-scroll-hidden' : ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={swipeX > 0 ? { transform: `translateX(${swipeX}px)`, opacity: Math.max(0, 1 - swipeX / 200) } : undefined}
    >
      {/* FAQ Panel */}
      {showFaq && !minimized && (
        <div className="ark-faq-panel">
          <div className="ark-faq-header">
            <span>Pregúntale a Arkana</span>
            <button className="ark-faq-close" onClick={toggleFaq}>×</button>
          </div>
          {faqAnswer !== null ? (
            <div className="ark-faq-answer">
              <p className="ark-faq-q">{FAQ[faqAnswer].q}</p>
              <p className="ark-faq-a">{FAQ[faqAnswer].a}</p>
              <button className="ark-faq-back" onClick={() => setFaqAnswer(null)}>← Otras preguntas</button>
            </div>
          ) : (
            <div className="ark-faq-list">
              {FAQ.map((f, i) => (
                <button key={i} className="ark-faq-item" onClick={() => handleFaqClick(i)}>
                  {f.q}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tip Bubble */}
      {bubbleContent && !showFaq && !minimized && (
        <div className={`ark-bubble ${exiting ? 'ark-bubble-exit' : ''} ${mode === 'onboarding' ? 'ark-bubble-onboard' : ''}`} onClick={nextTip}>
          {bubbleContent}
          {bubbleFooter}
          <div className="ark-bubble-tail" />
        </div>
      )}

      {/* Sprite area */}
      <div className="ark-sprite-wrap">
        {!minimized && (
          <div className={`ark-controls ${showBubble || showFaq ? 'ark-controls-visible' : ''}`}>
            <button className={`ark-btn-faq ${showFaq ? 'ark-btn-faq-active' : ''}`} onClick={toggleFaq} title="Preguntas frecuentes">?</button>
            <button className={`ark-btn-sound ${soundOn ? '' : 'ark-sound-off'}`} onClick={toggleSound} title={soundOn ? 'Silenciar sonido' : 'Activar sonido'}>
              {soundOn ? '♪' : '♪̸'}
            </button>
            <button className="ark-btn-quiet" onClick={handleQuietMode} title="Modo silencioso">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" />
                <path d="M17 16.95A7 7 0 015 12v-2m14 0v2c0 .76-.12 1.5-.34 2.18" />
              </svg>
            </button>
            <button className="ark-btn-min" onClick={handleMinimize} title="Minimizar">−</button>
            <button className="ark-btn-close" onClick={handleDismiss} title="No mostrar más">×</button>
          </div>
        )}

        <div
          className={`ark-sprite ${nudge ? 'ark-nudge' : ''} ${showBubble || showFaq ? 'ark-active' : 'ark-idle'}`}
          onClick={handleSpriteClick}
          title={minimized ? 'Click para ver tips' : showBubble ? 'Siguiente tip' : 'Click para un tip'}
        >
          <img src="/assets/arkana-idle.gif" alt="Arkana" className="ark-img" draggable={false} />

          {quietMode && !showBubble && !showFaq && (
            <div className="ark-quiet-badge" title="Modo silencioso">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M1 1l22 22" />
              </svg>
            </div>
          )}

          {!minimized && (
            <>
              <div className="ark-name">
                Arkana
                <span className={`ark-state-dot ${showBubble || showFaq ? 'talking' : 'idle'}`} />
              </div>
              <div className="ark-swipe-hint">desliza →</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
